import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError, API } from "@/lib/api";
import { CheckCircle, Clock, Download } from "@phosphor-icons/react";
import PartnerLeadsPanel from "@/pages/PartnerLeadsPanel";

export default function PartnerDashboardPage() {
    const [data, setData] = useState(null);
    const [quality, setQuality] = useState(null);
    const [badges, setBadges] = useState(null);
    const [error, setError] = useState("");
    const [leadsRefreshKey, setLeadsRefreshKey] = useState(0);
    const [showPayoutDetail, setShowPayoutDetail] = useState(false);

    const load = useCallback(async () => {
        try {
            const [jobsRes, profRes] = await Promise.all([
                http.get("/partner/jobs"),
                http.get("/partner/profile").catch(() => ({ data: null })),
            ]);
            setData(jobsRes.data);
            setQuality(profRes.data?.quality || null);
            setBadges(profRes.data?.badges || null);
        } catch (e) { setError(formatApiError(e)); }
    }, []);

    useEffect(() => {
        // First load + post-Stripe lead-claim sync (legacy — kept for any in-flight redirects).
        const url = new URL(window.location.href);
        const leadClaimed = url.searchParams.get("lead_claimed");
        if (leadClaimed) {
            (async () => {
                try { await http.get(`/payments/status/${leadClaimed}`); } catch (e) { /* noop */ }
                url.searchParams.delete("lead_claimed");
                window.history.replaceState({}, "", url.pathname + (url.search || ""));
                await load();
                setLeadsRefreshKey((k) => k + 1);
            })();
        } else {
            load();
        }
    }, [load]);

    function downloadStatement() {
        window.open(`${API}/partner/statement.csv?_=${Date.now()}`, "_blank");
    }

    if (!data) return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500">Loading…</div>;

    // Jobs that will be included in this Friday's payout (status === "due")
    const payoutJobs = data.jobs.filter((j) => j.payout_status === "due");

    return (
        <div data-testid="partner-dashboard" className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                    <div className="label-eyebrow mb-3">/ Partner</div>
                    <h1 className="font-serif text-4xl sm:text-5xl">Your <span className="italic gold-text">earnings</span></h1>
                </div>
                <div className="flex gap-2">
                    <button data-testid="partner-statement" onClick={downloadStatement} className="btn-ghost text-sm">
                        <Download size={14} weight="bold" className="inline mr-2" /> CSV statement
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <button
                    data-testid="stat-due-this-friday"
                    onClick={() => setShowPayoutDetail((v) => !v)}
                    className={`text-left bg-slate-900/60 border p-5 rounded-sm transition group hover:border-amber-300/50 ${showPayoutDetail ? "border-amber-300/60 bg-amber-500/5" : "border-white/10"}`}
                >
                    <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center justify-between">
                        <span>Due this Friday</span>
                        <span data-testid="due-toggle-caret" className={`text-amber-300/70 transition-transform ${showPayoutDetail ? "rotate-180" : ""}`}>▾</span>
                    </div>
                    <div className="font-serif text-3xl mt-2 text-amber-300">${(data.next_payout_amount || 0).toFixed(2)}</div>
                    {data.next_payout_date && (
                        <div className="text-xs text-slate-500 mt-1">
                            Payout: <span className="text-amber-300/80">{data.next_payout_date}</span>{" "}
                            <span className="text-slate-600">· {payoutJobs.length} {payoutJobs.length === 1 ? "job" : "jobs"}</span>
                        </div>
                    )}
                </button>
                <Stat label="Paid lifetime" value={`$${data.total_paid.toFixed(2)}`} accent="text-emerald-300" />
                <Stat label="Completed jobs" value={data.jobs.length} accent="text-gold" />
            </div>

            {showPayoutDetail && (
                <div data-testid="payout-detail-panel" className="mb-10 rounded-sm border border-amber-300/30 bg-amber-500/5 p-5">
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
                        <div>
                            <div className="label-eyebrow text-amber-300 mb-1">/ Friday {data.next_payout_date || ""} payout</div>
                            <div className="text-sm text-slate-300">
                                {payoutJobs.length === 0
                                    ? "No jobs queued for this payout yet."
                                    : <>{payoutJobs.length} {payoutJobs.length === 1 ? "job" : "jobs"} ready · 70% of gross per job</>}
                            </div>
                        </div>
                        <div className="font-serif text-2xl text-amber-300">${(data.next_payout_amount || 0).toFixed(2)}</div>
                    </div>
                    {payoutJobs.length > 0 ? (
                        <div className="space-y-2">
                            {payoutJobs.map((j) => (
                                <div key={j.id} data-testid={`payout-detail-${j.id}`} className="flex items-center justify-between gap-3 bg-slate-950/40 border border-white/5 rounded-sm px-4 py-3 text-sm">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-slate-100 truncate">{j.title || j.client_name}</div>
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {j.service_date && <>{j.service_date} · </>}{j.category} · gross ${j.gross_amount.toFixed(2)}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-amber-300 font-medium">+${j.payout_amount.toFixed(2)}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">70%</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-slate-500 italic py-4">
                            Jobs you claim and complete this week will appear here. They roll into the Friday {data.next_payout_date || ""} payout once the client gives a 👍 or 7 days pass with no dispute.
                        </div>
                    )}
                </div>
            )}

            {badges?.top_rated && (
                <div data-testid="top-rated-banner" className="mb-6 rounded-sm border border-gold/40 bg-gradient-to-r from-gold/20 via-gold/10 to-transparent p-5 flex items-center gap-4 flex-wrap">
                    <div className="text-3xl" aria-hidden>🏆</div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="font-serif text-xl text-gold leading-tight">Top-rated Butler</div>
                        <div className="text-sm text-slate-300 mt-1">
                            You&apos;ve earned {badges.five_star_count}+ five-star ratings from clients. This badge boosts you to the top of lead matching in your service area.
                        </div>
                    </div>
                </div>
            )}
            {badges && !badges.top_rated && badges.five_star_count > 0 && (
                <div data-testid="top-rated-progress" className="mb-6 rounded-sm border border-white/10 bg-slate-900/40 p-4 flex items-center gap-4 flex-wrap text-sm">
                    <div className="text-slate-300">
                        <strong className="text-gold">{badges.five_star_count}</strong> of {badges.five_star_count + badges.stars_to_top_rated} five-star ratings toward the Top-rated Butler badge
                    </div>
                    <div className="flex-1 min-w-[180px] h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gold transition-all"
                            style={{ width: `${Math.min(100, Math.round((badges.five_star_count / (badges.five_star_count + badges.stars_to_top_rated)) * 100))}%` }}
                        />
                    </div>
                    <div className="text-xs text-slate-500">{badges.stars_to_top_rated} to go</div>
                </div>
            )}

            {quality && (
                <div data-testid="dash-quality-card" className="mb-6 rounded-sm border border-white/10 bg-slate-900/50 p-5 flex items-center gap-5 flex-wrap">
                    <div className="text-center">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Quality</div>
                        <div data-testid="dash-quality-score" className="font-serif text-4xl text-gold leading-none mt-1">{quality.score}<span className="text-base text-slate-500">/100</span></div>
                    </div>
                    <div className="flex-1 min-w-[220px] text-sm text-slate-300">
                        {quality.tips && quality.tips.length > 0 ? (
                            <div>
                                <div className="text-xs text-slate-500 mb-1">Next boost:</div>
                                <div className="text-slate-200">{quality.tips[0]}</div>
                            </div>
                        ) : (
                            <div className="text-emerald-300">Profile is complete — top tier visibility.</div>
                        )}
                    </div>
                    <Link to="/partner/profile" data-testid="dash-edit-profile" className="btn-ghost text-xs">Edit profile →</Link>
                </div>
            )}

            <div data-testid="payout-info-card" className="mb-8 rounded-sm border border-gold/30 bg-gradient-to-br from-gold/5 to-transparent p-6">
                <div className="label-eyebrow text-gold mb-2">/ How you get paid</div>
                <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-gold mt-1 shrink-0" /> <span><strong className="text-white">70%</strong> of every completed cleaning job — paid out every <strong className="text-white">Friday</strong>.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-gold mt-1 shrink-0" /> <span><strong className="text-white">100%</strong> of client tips — passed through, no platform cut.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-gold mt-1 shrink-0" /> <span><strong className="text-white">$0</strong> membership. <strong className="text-white">$0</strong> per-lead fee. Ever.</span></li>
                    <li className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-gold mt-1 shrink-0" /> <span>Acceptance = client 👍 rating OR 7 days with no dispute.</span></li>
                </ul>
            </div>

            {/* Phase 3 — Exclusive Lead Routing */}
            <section className="mb-12" data-testid="leads-section">
                <div className="mb-5 flex items-center justify-between">
                    <div>
                        <div className="label-eyebrow mb-2">/ Leads</div>
                        <h2 className="font-serif text-3xl">Exclusive <span className="italic gold-text">opportunities</span></h2>
                    </div>
                </div>
                <PartnerLeadsPanel key={leadsRefreshKey} />
            </section>

            <section className="mb-12" data-testid="earnings-section">
                <div className="mb-5">
                    <div className="label-eyebrow mb-2">/ Earnings</div>
                    <h2 className="font-serif text-3xl">Recent <span className="italic gold-text">cleaning jobs</span></h2>
                </div>
                {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
                {data.jobs.length === 0 ? (
                    <div className="bg-slate-900/40 border border-white/5 p-16 rounded-sm text-center text-slate-400">
                        No claimed cleaning jobs yet. New jobs appear here as soon as you claim a lead.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.jobs.map((j) => <EarningsRow key={j.id} job={j} />)}
                    </div>
                )}
            </section>
        </div>
    );
}

function EarningsRow({ job }) {
    const statusLabel = {
        paid: { label: "Paid", className: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
        due: { label: "Due Friday", className: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
        pending_acceptance: { label: "Pending acceptance", className: "bg-slate-700/40 text-slate-300 border-white/10" },
    }[job.payout_status] || { label: job.payout_status, className: "bg-slate-700/40 text-slate-300 border-white/10" };

    return (
        <div data-testid={`job-row-${job.id}`} className="bg-slate-900/40 border border-white/5 rounded-sm p-5 flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
                <div className="font-medium">{job.title || job.client_name}</div>
                <div className="text-xs text-slate-500 mt-1">
                    {job.service_date && <>📅 {job.service_date} · </>}
                    {job.category} · gross ${job.gross_amount.toFixed(2)}
                </div>
            </div>
            <div className="text-right">
                <div className="text-xs text-slate-500 uppercase tracking-widest">Your payout (70%)</div>
                <div className="font-serif text-2xl gold-text">${job.payout_amount.toFixed(2)}</div>
            </div>
            <div className="min-w-[140px] text-right">
                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm border ${statusLabel.className}`}>
                    {job.payout_status === "paid"
                        ? <CheckCircle size={14} weight="fill" />
                        : <Clock size={14} weight="fill" />}
                    {statusLabel.label}
                </span>
                {job.payout_recorded_at && (
                    <div className="text-xs text-slate-500 mt-1">{job.payout_recorded_at.slice(0, 10)}</div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, accent, sub }) {
    return (
        <div className="bg-slate-900/60 border border-white/10 p-5 rounded-sm">
            <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
            <div className={`font-serif text-3xl mt-2 ${accent}`}>{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}
